import { CLOUD_PROVIDERS } from '@couch/shared';
import { Link } from 'react-router';
import { CopyField } from '../../components/CopyField';
import { CloudCostWarning, ConsoleLink } from '../machines/CloudBadge';
import { RunPodKeyPanel } from './RunPodKeyPanel';

/** RunPod: a GPU machine for Couch, and RunPod in Claude Code (doc 54). */
export function RunPodSection() {
  return (
    <section className="panel" aria-labelledby="runpod-title">
      <h2 id="runpod-title">RunPod — a GPU machine for Couch</h2>
      <p>A pod is a rented Linux machine with a GPU. Couch runs on it like on your own computer — no key goes into Couch.</p>
      <CloudCostWarning />
      <ConsoleLink href={CLOUD_PROVIDERS.runpod.console} provider="runpod" />
      <p>
        <strong>Easiest:</strong> keep your RunPod key on a home machine or on Couch (below), then My machines → <strong>New cloud
        machine</strong> — Couch creates the pod and installs itself on it. A monthly limit: Account → Cloud spending limit.
      </p>
      <p>Or by hand:</p>
      <ol className="connection-steps">
        <li>Add credit in RunPod (Billing). Keep it small: it is your spending limit.</li>
        <li>
          Deploy a pod in a <strong>datacenter in Europe</strong> (names start with EU), e.g. an L4 or RTX 4090, the template
          <strong> RunPod PyTorch</strong>, a <strong>volume disk</strong> at <code>/workspace</code> — not a network volume:
          programs cannot run from a network volume.
        </li>
        <li>In Couch: My machines → Add machine → <strong>Linux</strong> → Show the command.</li>
        <li>In RunPod: your pod → Connect → Web terminal, paste the command. The pod shows up as a Cloud machine.</li>
        <li>It stops itself when idle (machine page). Check the RunPod console yourself, and terminate the pod when you are done.</li>
      </ol>
      <p>Step by step, with costs: the <Link to="/knowledge/cloud-gpu">cloud GPU guide</Link>.</p>

      <RunPodKeyPanel />

      <h3>RunPod in Claude Code (optional)</h3>
      <p>Lets Claude create, check and stop pods for you. Install RunPod&apos;s plugin in a terminal:</p>
      <CopyField value="claude plugin marketplace add runpod/runpod-plugins-official" label="Add RunPod's plugin source" />
      <CopyField value="claude plugin install runpod@runpod" label="Install the RunPod plugin" />
      <p>
        Then sign in from Claude Code <strong>in a terminal</strong>: start <code>claude</code>, type <code>/mcp</code>, choose runpod →
        Authenticate. (As of October 2026, RunPod refuses the sign-in from the Claude desktop app.)
      </p>
      <p>
        Or with an API key from RunPod (Settings → API Keys), kept on your computer — never into a chat or into Couch:
      </p>
      <CopyField value="read -s RUNPOD_API_KEY" label="Type the key without showing it" />
      <CopyField
        value={'claude mcp add --transport http runpod -s user https://mcp.getrunpod.io/ --header "Authorization: Bearer $RUNPOD_API_KEY"'}
        label="Add RunPod's server with the key"
      />
      <CopyField value="unset RUNPOD_API_KEY" label="Forget the key in this terminal" />
    </section>
  );
}
